// Buy Box Agents card (Buy Box › Agents): up to 3 agents, each with a
// permanent number (Agent 1/2/3), one saved Buy Box version, one city, a
// "new listings" lookback window, an on/off switch and its last-run status.
// Free accounts see the Host upsell and can't turn one on. "Preview" runs a
// one-off scan with an agent's settings (no matches recorded, no email).

import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { Bell, Eye, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  BUY_BOX_AGENT_LOOKBACK_OPTIONS,
  BUY_BOX_AGENT_UPSELL,
  DEFAULT_BUY_BOX_AGENT_LOOKBACK_DAYS,
  MAX_BUY_BOX_AGENTS,
  buyBoxVersionLabel,
} from "@shared/buy-box-agents";
import type { BuyBoxAgent, BuyBoxVersion } from "@shared/schema";

export type AgentsResponse = {
  agents: BuyBoxAgent[];
  max: number;
  entitlement: { eligible: boolean; reason?: string };
  upsell: string | null;
  accountDisabled: boolean;
  globalEnabled: boolean;
  timezone: string | null;
};

export type VersionWithUsage = BuyBoxVersion & { missing: string[]; usedBy: Array<number | null> };
export type VersionsResponse = { versions: VersionWithUsage[] };

export const AGENTS_KEY = ["/api/app/buy-box/agents"];
export const VERSIONS_KEY = ["/api/app/buy-box/versions"];

type Draft = { city: string; state: string; versionId: string; lookbackDays: string };

export function lookbackLabel(days: number): string {
  return days === 1 ? "Listed in the last day" : `Listed in the last ${days} days`;
}

const selectClass = "mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm";

export type AgentPreviewSummary = {
  reportId: number;
  status: string;
  statusNote: string | null;
  done: boolean;
  totalFound: number;
  matching: number;
  newMatching: number;
  alreadySent: number;
};

function plural(n: number, one: string, many: string) {
  return `${n} ${n === 1 ? one : many}`;
}

function AgentPreviewPanel({ agentId, reportId, lookbackDays, onClose }: { agentId: number; reportId: number; lookbackDays: number; onClose: () => void }) {
  const q = useQuery<AgentPreviewSummary>({
    queryKey: [`/api/app/buy-box/agents/${agentId}/preview/${reportId}`],
    refetchInterval: (query) => (query.state.data?.done || query.state.error ? false : 4000),
  });
  const p = q.data;
  let body: ReactNode;
  if (q.isError) {
    body = <span className="text-red-700">{parseErrorMessage(q.error, "Couldn't load the preview.")}</span>;
  } else if (!p || !p.done) {
    body = (
      <span className="inline-flex items-center gap-2 text-gray-600">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Scanning newly listed houses… this can take a few minutes.
      </span>
    );
  } else if (p.status === "failed") {
    body = <span className="text-red-700">The preview scan couldn't finish{p.statusNote ? `: ${p.statusNote}` : "."}</span>;
  } else {
    body = (
      <span className="text-gray-800">
        <span className="font-semibold" data-testid={`text-buy-box-agent-preview-count-${agentId}`}>{plural(p.matching, "house", "houses")}</span>{" "}
        {lookbackLabel(lookbackDays).toLowerCase()} {p.matching === 1 ? "matches" : "match"} today
        {p.matching > 0 && (
          <>
            {" "}({p.newMatching} new to you{p.alreadySent > 0 ? `, ${p.alreadySent} already sent` : ""})
          </>
        )}
        . <span className="text-gray-500">Out of {plural(p.totalFound, "new listing", "new listings")} scanned.</span>{" "}
        <Link href={`/app/buy-box/reports/${p.reportId}`}>
          <span className="text-blue-600 hover:underline cursor-pointer" data-testid={`link-buy-box-agent-preview-report-${agentId}`}>View houses</span>
        </Link>
      </span>
    );
  }
  return (
    <div className="mt-2 flex items-start justify-between gap-2 rounded-md bg-blue-50 px-3 py-2 text-xs" data-testid={`panel-buy-box-agent-preview-${agentId}`}>
      <div>
        {body}
        <div className="mt-1 text-[11px] text-gray-500">Preview only — nothing is saved to New matches and no email is sent.</div>
      </div>
      <button type="button" onClick={onClose} aria-label="Close preview" className="text-gray-400 hover:text-gray-600" data-testid={`button-close-buy-box-agent-preview-${agentId}`}>
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function AgentForm({
  initial,
  versions,
  pending,
  submitLabel,
  onCancel,
  onSubmit,
  testIdPrefix,
}: {
  initial: Draft;
  versions: VersionWithUsage[];
  pending: boolean;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (d: Draft) => void;
  testIdPrefix: string;
}) {
  const [draft, setDraft] = useState<Draft>(initial);
  const set = (k: keyof Draft, v: string) => setDraft((d) => ({ ...d, [k]: v }));
  const chosen = versions.find((v) => String(v.id) === draft.versionId);
  return (
    <form
      className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-lg border border-gray-200 p-4 mb-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(draft);
      }}
      data-testid={`form-${testIdPrefix}`}
    >
      <div className="grid grid-cols-3 gap-2 sm:col-span-2">
        <label className="block text-xs text-gray-600 col-span-2">
          City
          <Input value={draft.city} onChange={(e) => set("city", e.target.value)} className="mt-1 h-9" required data-testid={`input-${testIdPrefix}-city`} />
        </label>
        <label className="block text-xs text-gray-600">
          State
          <Input value={draft.state} onChange={(e) => set("state", e.target.value)} className="mt-1 h-9" required maxLength={2} placeholder="GA" data-testid={`input-${testIdPrefix}-state`} />
        </label>
      </div>
      <label className="block text-xs text-gray-600">
        Buy Box version
        <select className={selectClass} value={draft.versionId} onChange={(e) => set("versionId", e.target.value)} data-testid={`select-${testIdPrefix}-version`}>
          {versions.map((v) => (
            <option key={v.id} value={String(v.id)}>
              {buyBoxVersionLabel(v.versionNumber, v.label)}{v.missing.length > 0 ? " — incomplete" : ""}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-xs text-gray-600">
        New listings window
        <select className={selectClass} value={draft.lookbackDays} onChange={(e) => set("lookbackDays", e.target.value)} data-testid={`select-${testIdPrefix}-lookback`}>
          {BUY_BOX_AGENT_LOOKBACK_OPTIONS.map((d) => (
            <option key={d} value={String(d)}>{lookbackLabel(d)}</option>
          ))}
        </select>
      </label>
      {chosen && chosen.missing.length > 0 && (
        <p className="sm:col-span-2 text-xs text-amber-700" data-testid={`text-${testIdPrefix}-version-incomplete`}>
          {buyBoxVersionLabel(chosen.versionNumber, chosen.label)} is missing required fields. Complete it in Buy Box versions before an agent can use it.
        </p>
      )}
      <div className="sm:col-span-2 flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={pending} data-testid={`button-save-${testIdPrefix}`}>{submitLabel}</Button>
      </div>
    </form>
  );
}

export function BuyBoxAgentsCard({ readOnly = false }: { readOnly?: boolean }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [previews, setPreviews] = useState<Record<number, number>>({});
  const q = useQuery<AgentsResponse>({ queryKey: AGENTS_KEY });
  const vq = useQuery<VersionsResponse>({ queryKey: VERSIONS_KEY });
  const versions = vq.data?.versions ?? [];
  const versionById = new Map(versions.map((v) => [v.id, v]));

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: AGENTS_KEY });
    void qc.invalidateQueries({ queryKey: VERSIONS_KEY });
  };
  const onError = (err: unknown) => toast({ title: "Couldn't save the agent", description: parseErrorMessage(err, "Please try again."), variant: "destructive" });

  const toBody = (d: Draft) => ({
    city: d.city,
    state: d.state,
    versionId: d.versionId ? Number(d.versionId) : undefined,
    lookbackDays: Number(d.lookbackDays),
  });

  const create = useMutation({
    mutationFn: async (d: Draft) => (await apiRequest("POST", "/api/app/buy-box/agents", toBody(d))).json(),
    onSuccess: () => {
      setAdding(false);
      toast({ title: "Buy Box Agent saved", description: "It runs every morning around 6am and emails you only newly listed houses you haven't seen." });
      invalidate();
    },
    onError,
  });
  const update = useMutation({
    mutationFn: async ({ id, d }: { id: number; d: Draft }) => (await apiRequest("PATCH", `/api/app/buy-box/agents/${id}`, toBody(d))).json(),
    onSuccess: () => {
      setEditingId(null);
      invalidate();
    },
    onError,
  });
  const toggle = useMutation({
    mutationFn: async ({ id, active }: { id: number; active: boolean }) => (await apiRequest("PATCH", `/api/app/buy-box/agents/${id}`, { active })).json(),
    onSuccess: () => invalidate(),
    onError,
  });
  const preview = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => ({ id, ...(await (await apiRequest("POST", `/api/app/buy-box/agents/${id}/preview`)).json()) as { reportId: number } }),
    onSuccess: ({ id, reportId }) => setPreviews((p) => ({ ...p, [id]: reportId })),
    onError: (err: unknown) => toast({ title: "Couldn't start the preview", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });
  const remove = useMutation({
    mutationFn: async (id: number) => (await apiRequest("DELETE", `/api/app/buy-box/agents/${id}`)).json(),
    onSuccess: () => invalidate(),
    onError,
  });

  const data = q.data;
  const eligible = data?.entitlement.eligible === true;
  const agents = data?.agents ?? [];
  const max = data?.max ?? MAX_BUY_BOX_AGENTS;
  // `readOnly` = the viewer has view-only Buy Box access (teammate permission).
  const canAdd = eligible && !readOnly && agents.length < max;
  const canTurnOn = eligible && !readOnly;
  const v1 = versions.find((v) => v.versionNumber === 1);

  const agentTitle = (a: BuyBoxAgent) => (a.agentNumber != null ? `Agent ${a.agentNumber}` : a.name);

  const agentList = (
    <ul className="divide-y divide-gray-100 mb-3" data-testid="list-buy-box-agents">
      {agents.length === 0 && <li className="py-3 text-sm text-gray-500">No agents yet.</li>}
      {agents.map((a) => {
        const version = a.versionId != null ? versionById.get(a.versionId) : undefined;
        if (editingId === a.id) {
          return (
            <li key={a.id} className="py-3" data-testid={`row-buy-box-agent-${a.id}`}>
              <AgentForm
                initial={{ city: a.city, state: a.state, versionId: String(a.versionId ?? v1?.id ?? ""), lookbackDays: String(a.lookbackDays ?? DEFAULT_BUY_BOX_AGENT_LOOKBACK_DAYS) }}
                versions={versions}
                pending={update.isPending}
                submitLabel="Save changes"
                onCancel={() => setEditingId(null)}
                onSubmit={(d) => update.mutate({ id: a.id, d })}
                testIdPrefix={`edit-agent-${a.id}`}
              />
            </li>
          );
        }
        return (
          <li key={a.id} className="py-3" data-testid={`row-buy-box-agent-${a.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium text-gray-900 truncate" data-testid={`text-buy-box-agent-title-${a.id}`}>
                  {agentTitle(a)}
                  <span className="ml-2 inline-flex items-center rounded bg-blue-50 px-1.5 py-0.5 text-[11px] font-medium text-blue-700">
                    {version ? buyBoxVersionLabel(version.versionNumber, version.label) : "Buy Box"}
                  </span>
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {a.city}, {a.state} · {lookbackLabel(a.lookbackDays ?? DEFAULT_BUY_BOX_AGENT_LOOKBACK_DAYS)}
                </div>
                {version && version.missing.length > 0 && (
                  <div className="text-xs text-amber-700" data-testid={`text-buy-box-agent-version-incomplete-${a.id}`}>
                    {buyBoxVersionLabel(version.versionNumber, version.label)} is missing required fields.
                  </div>
                )}
                <div className="text-xs text-gray-400" data-testid={`text-buy-box-agent-last-run-${a.id}`}>
                  {a.lastRunAt
                    ? `Last run ${new Date(a.lastRunAt).toLocaleString()} · ${a.lastRunError ? "couldn't finish" : `${a.lastRunMatches ?? 0} new`}`
                    : "Hasn't run yet"}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={readOnly || !eligible || (version != null && version.missing.length > 0) || (preview.isPending && preview.variables === a.id)}
                  onClick={() => preview.mutate(a.id)}
                  aria-label={`Preview ${agentTitle(a)}`}
                  data-testid={`button-preview-buy-box-agent-${a.id}`}
                >
                  {preview.isPending && preview.variables === a.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
                  Preview
                </Button>
                <Switch
                  checked={a.active}
                  disabled={readOnly || toggle.isPending || (!a.active && !canTurnOn)}
                  onCheckedChange={(active) => toggle.mutate({ id: a.id, active })}
                  aria-label={a.active ? `Pause ${agentTitle(a)}` : `Turn on ${agentTitle(a)}`}
                  data-testid={`switch-buy-box-agent-${a.id}`}
                />
                <Button variant="ghost" size="icon" disabled={readOnly || !eligible} onClick={() => setEditingId(a.id)} aria-label={`Edit ${agentTitle(a)}`} data-testid={`button-edit-buy-box-agent-${a.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" disabled={readOnly || remove.isPending} onClick={() => remove.mutate(a.id)} aria-label={`Delete ${agentTitle(a)}`} data-testid={`button-delete-buy-box-agent-${a.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {previews[a.id] != null && (
              <AgentPreviewPanel
                agentId={a.id}
                reportId={previews[a.id]}
                lookbackDays={a.lookbackDays ?? DEFAULT_BUY_BOX_AGENT_LOOKBACK_DAYS}
                onClose={() => setPreviews(({ [a.id]: _closed, ...rest }) => rest)}
              />
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6" data-testid="card-buy-box-agents" data-tour="buy-box-agents-list">
      <div className="flex items-center justify-between mb-2 gap-3">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-blue-600" />
          <h2 className="font-semibold text-gray-900">Buy Box Agents</h2>
        </div>
        <Link href="/app/buy-box/matches">
          <span className="text-sm text-blue-600 hover:underline cursor-pointer" data-testid="link-buy-box-matches">New matches</span>
        </Link>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Up to {max} agents each run one Buy Box version against one city every morning around 6am ({data?.timezone ?? "your account timezone"}).
        They only look at newly listed houses and email you houses you haven't been sent before.
      </p>

      {q.isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : !eligible ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="text-buy-box-agents-upsell">
          {data?.upsell ?? BUY_BOX_AGENT_UPSELL}{" "}
          <Link href="/app/billing"><span className="font-medium underline cursor-pointer">See plans</span></Link>
        </div>
      ) : null}
      {!q.isLoading && !eligible && agents.length > 0 && (
        <div className="mt-3">
          <p className="text-xs text-gray-500">
            Your saved agents don't run on this plan.{readOnly ? "" : " You can still pause or delete them."}
          </p>
          {agentList}
        </div>
      )}
      {q.isLoading || !eligible ? null : (
        <>
          {(data?.accountDisabled || data?.globalEnabled === false) && (
            <p className="mb-3 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600" data-testid="text-buy-box-agents-paused-by-admin">
              Daily runs are paused for your account right now. Your agents are saved and will resume automatically.
            </p>
          )}
          {agentList}
          {adding ? (
            <AgentForm
              initial={{ city: "", state: "", versionId: String(v1?.id ?? versions[0]?.id ?? ""), lookbackDays: String(DEFAULT_BUY_BOX_AGENT_LOOKBACK_DAYS) }}
              versions={versions}
              pending={create.isPending}
              submitLabel="Save agent"
              onCancel={() => setAdding(false)}
              onSubmit={(d) => create.mutate(d)}
              testIdPrefix="buy-box-agent"
            />
          ) : (
            <Button variant="outline" size="sm" disabled={!canAdd || versions.length === 0} onClick={() => setAdding(true)} data-testid="button-add-buy-box-agent">
              <Plus className="h-4 w-4 mr-1" />
              {agents.length >= max ? `All ${max} agents in use` : "Add a Buy Box Agent"}
            </Button>
          )}
        </>
      )}
    </div>
  );
}
